(function (scope) {
  'use strict';
  const clone = (value) => JSON.parse(JSON.stringify(value));
  const giftSpec = (line) => {
    try {
      const gifts = JSON.parse(line.properties?._pr_gifts || '[]');
      return Array.isArray(gifts) ? gifts.filter((gift) => /^\d+$/.test(String(gift.id)) && Number(gift.quantity) > 0) : [];
    } catch (_) { return []; }
  };
  const offerKey = (line) => {
    if (!line.properties?._pr_offer) return null;
    const plan = line.selling_plan_allocation?.selling_plan?.id || line.properties._pr_plan_id;
    return `${line.properties._pr_offer}:${line.variant_id}${plan ? `:plan-${plan}` : ''}`;
  };
  const isGift = (line) => line.properties?._pr_gift === 'true';
  function giftPlan(cart) {
    const required = new Map();
    for (const line of cart.items) {
      if (!offerKey(line) || isGift(line)) continue;
      const units = Math.max(1, Number(line.properties._pr_units) || 1);
      for (const gift of giftSpec(line)) {
        const identity = `${offerKey(line)}:${gift.id}`;
        const entry = required.get(identity) || { id: String(gift.id), parent: offerKey(line), quantity: 0 };
        entry.quantity += line.quantity / units * Number(gift.quantity);
        required.set(identity, entry);
      }
    }
    for (const entry of required.values()) entry.quantity = Math.floor(entry.quantity);
    const updates = {};
    const found = new Set();
    for (const line of cart.items.filter(isGift)) {
      const identity = `${line.properties._pr_parent}:${line.variant_id}`;
      const expected = required.get(identity);
      if (!expected || found.has(identity)) updates[line.key] = 0;
      else {
        found.add(identity);
        if (line.quantity !== expected.quantity) updates[line.key] = expected.quantity;
      }
    }
    const additions = [...required.entries()].filter(([id, entry]) => !found.has(id) && entry.quantity > 0).map(([, entry]) => ({ id: Number(entry.id), quantity: entry.quantity, properties: { _pr_gift: 'true', _pr_parent: entry.parent } }));
    return { updates, additions };
  }
  const presentationKey = (variant, units, plan) => `${variant}:${units || 1}:${plan || ''}`;
  // Speed: the drawer section rendered in the same response as cart/add.js ("sections" parameter).
  const CART_SECTION = 'pr-cart-drawer';
  // A cart mutation response that already is the whole cart (cart/change.js, cart/update.js), or the cart
  // embedded in the drawer section returned with cart/add.js. Anything else returns null (read cart.js instead).
  const cartFromResponse = (data) => {
    if (data && Array.isArray(data.items) && typeof data.item_count === 'number' && typeof data.total_price === 'number' && typeof data.currency === 'string') return data;
    const html = data?.sections?.[CART_SECTION];
    if (typeof html !== 'string') return null;
    const start = html.indexOf('>', html.indexOf('data-pr-cart-config')) + 1;
    const end = html.indexOf('</script>', start);
    if (start < 1 || end < 0) return null;
    try {
      const cart = JSON.parse(html.slice(start, end)).cart;
      return cart && Array.isArray(cart.items) && typeof cart.total_price === 'number' ? cart : null;
    } catch (_) { return null; }
  };
  const readJSON = (value, fallback) => { try { return JSON.parse(value); } catch (_) { return fallback; } };
  const offerProperties = (offer) => {
    const units = Math.max(1, Number(offer.quantity) || 1);
    const manualGifts = offer.subscription && offer.subscriptionManualGifts ? (offer.gifts || []) : [];
    const gifts = (offer.giftIncludedInBundle || manualGifts.length ? [] : (offer.gifts || [])).map(gift => ({ id: String(gift.variantId), quantity: Math.max(1, Number(gift.quantity) || 1) }));
    const properties = { _pr_offer: String(offer.offerId || offer.id), _pr_units: String(units), _pr_label: offer.label || '', _pr_gifts: JSON.stringify(gifts), _pr_compare_at: String(Number(offer.compareAtPrice) || 0), _pr_currency: offer.currency || '' };
    if (offer.benefits?.length) properties._pr_benefits = JSON.stringify(offer.benefits);
    if (offer.giftIncludedInBundle && offer.gifts?.length) properties._pr_display_gifts = JSON.stringify(offer.gifts);
    if (offer.subscription) {
      properties._pr_plan_id = String(offer.sellingPlanId);
      properties._pr_title = offer.title || offer.label || '';
      properties._pr_image = offer.image || '';
      if (manualGifts.length) {
        properties._pr_manual_gifts = JSON.stringify(manualGifts);
        properties['Included gift'] = manualGifts.map(gift => `${Math.max(1, Number(gift.quantity) || 1)} × ${gift.title} free per ${units} bags, every delivery`).join('; ');
      }
    }
    return properties;
  };
  function rowsFor(cart, benefitsByOffer = {}, offersByKey = {}) {
    const rows = [];
    const groups = new Map();
    for (const line of cart.items) {
      const group = !isGift(line) && offerKey(line);
      if (!group) { rows.push({ ...line, keys: [line.key], units: 1, count: line.quantity }); continue; }
      if (!groups.has(group)) {
        const row = { ...line, quantity: 0, final_line_price: 0, original_line_price: 0, keys: [], group, units: Math.max(1, Number(line.properties._pr_units) || 1) };
        groups.set(group, row);
        rows.push(row);
      }
      const row = groups.get(group);
      row.quantity += line.quantity;
      row.final_line_price += line.final_line_price;
      row.original_line_price += line.original_line_price;
      row.keys.push(line.key);
      row.count = row.quantity / row.units;
    }
    for (const row of rows) {
      const key = presentationKey(row.variant_id, row.units, row.selling_plan_allocation?.selling_plan?.id || row.properties?._pr_plan_id);
      const current = offersByKey[key];
      row.presentation = current?.currency === cart.currency ? current : null;
      const storedCompare = row.properties?._pr_currency === cart.currency ? Number(row.properties._pr_compare_at) : 0;
      row.compare_line_price = Math.max(row.original_line_price || 0, (Number(row.presentation?.compareAtPrice) || storedCompare || 0) * row.quantity);
    }
    for (const row of [...rows]) {
      let manualGifts = readJSON(row.properties?._pr_manual_gifts || row.properties?._pr_display_gifts || '[]', []);
      if (row.presentation && (row.presentation.giftIncludedInBundle || row.presentation.subscriptionManualGifts)) manualGifts = row.presentation.gifts || [];
      if (!row.group || !Array.isArray(manualGifts)) continue;
      for (const [index, gift] of manualGifts.entries()) {
        const quantity = Math.floor(row.count) * Math.max(1, Number(gift.quantity) || 1);
        rows.splice(rows.indexOf(row) + index + 1, 0, { key: `${row.group}:manual-gift-${index}`, keys: [], units: 1, count: quantity, title: gift.title, product_title: gift.title, image: gift.image, quantity, final_line_price: 0, original_line_price: (Number(gift.price) || 0) * quantity, properties: { _pr_gift: 'true', _pr_parent: row.group } });
      }
    }
    for (const row of [...rows]) {
      if (!row.group) continue;
      const benefits = row.presentation?.benefits ?? benefitsByOffer[String(row.properties._pr_offer).split(':subscription-')[0]] ?? readJSON(row.properties._pr_benefits || '[]', []);
      if (!Array.isArray(benefits)) continue;
      const parentIndex = rows.indexOf(row);
      let insertion = parentIndex + 1;
      while (insertion < rows.length && rows[insertion].properties?._pr_parent === row.group) insertion++;
      for (const benefit of benefits.filter(item => item && ['truck', 'shield'].includes(item.kind))) {
        const quantity = Math.floor(row.count);
        rows.splice(insertion++, 0, { key: `${row.group}:benefit-${benefit.kind}`, keys: [], units: 1, count: quantity, quantity, title: benefit.title, product_title: benefit.title, image: benefit.image, benefitKind: benefit.kind, final_line_price: 0, original_line_price: benefit.kind === 'truck' ? 500 * quantity : 0, properties: { _pr_gift: 'true', _pr_parent: row.group } });
      }
    }
    return rows.sort((a, b) => {
      const parentA = a.group || a.properties?._pr_parent || a.key;
      const parentB = b.group || b.properties?._pr_parent || b.key;
      if (parentA === parentB) return Number(isGift(a)) - Number(isGift(b));
      return 0;
    });
  }
  class CartEngine {
    constructor(options = {}) {
      // Speed/tracking: window.fetch is looked up for every request, so a wrapper installed later (Shopify's cart
      // event listener for add-to-cart analytics) always sees the cart requests, however early this engine starts.
      this.transport = options.transport || ((...args) => scope.fetch(...args));
      this.root = options.root || '/';
      this.cart = options.previewMode !== true && Array.isArray(options.initialCart?.items) ? clone(options.initialCart) : { items: [], total_price: 0, original_total_price: 0, item_count: 0, currency: options.currency || 'USD' };
      this.pendingAdds = new Map();
      this.nextAdd = 0;
      this.preview = options.previewMode === true;
      this.previewMode = options.previewMode;
      this.previewCurrency = options.previewCurrency || 'USD';
      this.previewOffers = new Map();
      this.tail = Promise.resolve();
      this.onUpdate = options.onUpdate || (() => {});
      this.onBusy = options.onBusy || (() => {});
      this.pending = 0;
      // Speed: which cart state the server confirmed (gifts reconciled) and when; see checkoutReady().
      this.version = 0;
      this.confirmedVersion = -1;
      this.confirmedAt = 0;
      this.invalidations = 0;
    }
    setCart(cart) { this.cart = cart; this.version += 1; }
    markStale() { this.confirmedVersion = -1; this.invalidations += 1; }
    // True while the cart shown is exactly the one the server confirmed within the last minute.
    isConfirmed() { return !this.preview && this.pendingAdds.size === 0 && this.confirmedVersion === this.version && Date.now() - this.confirmedAt < 60000; }
    enqueue(operation) {
      this.pending += 1;
      this.onBusy(true);
      const result = this.tail.then(operation);
      this.tail = result.catch(() => {});
      return result.finally(() => { this.pending -= 1; this.onBusy(this.pending > 0); });
    }
    async request(path, body) {
      // Speed: any write ends the confirmed state until the next successful reconcile; a failure also invalidates it.
      if (body) this.confirmedVersion = -1;
      try {
        const response = await this.transport(`${this.root}${path}`, body ? { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) } : { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
        const data = await response.json();
        if (!response.ok || data.status) {
          const error = new Error(data.description || data.message || 'cart_error');
          error.rejected = response.status === 400; // Shopify refused the request itself (e.g. an unknown line key): nothing changed.
          throw error;
        }
        return data;
      } catch (error) { this.markStale(); throw error; }
    }
    emit() {
      const cart = clone(this.cart);
      for (const [token, offer] of this.pendingAdds) {
        const quantity = Math.max(1, Number(offer.quantity) || 1);
        const properties = offerProperties(offer);
        const price = Math.round(Number(offer.unitPrice) || 0) * quantity;
        const line = { key: `pending:${token}`, variant_id: offer.variantId, title: offer.title || offer.label, product_title: offer.title || offer.label, image: offer.image, quantity, final_line_price: price, original_line_price: price, properties };
        if (offer.subscription) line.selling_plan_allocation = { selling_plan: { id: offer.sellingPlanId, name: offer.subscription.label } };
        cart.items.push(line);
        cart.total_price += price;
        cart.original_total_price = (cart.original_total_price || 0) + price;
        cart.item_count += quantity;
        const parent = offerKey(line);
        if (!offer.giftIncludedInBundle && !offer.subscriptionManualGifts) (offer.gifts || []).forEach((gift, index) => {
          const count = Math.max(1, Number(gift.quantity) || 1);
          cart.items.push({ key: `pending:${token}:gift:${index}`, variant_id: gift.variantId, title: gift.title, product_title: gift.title, image: gift.image, quantity: count, final_line_price: 0, original_line_price: (Number(gift.price) || 0) * count, properties: { _pr_gift: 'true', _pr_parent: parent } });
          cart.item_count += count;
        });
      }
      cart.optimistic = this.pendingAdds.size > 0;
      this.onUpdate(cart, this.preview);
      return cart;
    }
    async read(publish = true) { this.setCart(await this.request('cart.js')); if (publish) this.emit(); return this.cart; }
    // `known` is a cart the server just returned with a mutation; it replaces the separate cart.js read.
    // `generation` is the invalidation count from before the operation's first request (see markStale()).
    async reconcile(publish = true, known = null, generation = this.invalidations) {
      if (known) this.setCart(known); else await this.read(false);
      const plan = giftPlan(this.cart);
      if (Object.keys(plan.updates).length) await this.request('cart/update.js', { updates: plan.updates });
      if (plan.additions.length) await this.request('cart/add.js', { items: plan.additions });
      if (Object.keys(plan.updates).length || plan.additions.length) await this.read(false);
      // Speed: confirmed only when nothing invalidated the cart meanwhile and its gifts are complete.
      const remaining = giftPlan(this.cart);
      if (generation === this.invalidations && !Object.keys(remaining.updates).length && !remaining.additions.length) {
        this.confirmedVersion = this.version;
        this.confirmedAt = Date.now();
      }
      if (publish) this.emit();
      return this.cart;
    }
    async mutate(operation) {
      const generation = this.invalidations;
      try { const data = await operation(); return await this.reconcile(true, cartFromResponse(data), generation); }
      catch (error) { await this.read().catch(() => {}); throw error; }
    }
    isConfigured(offer) { return Number.isSafeInteger(Number(offer.variantId)) && Number(offer.variantId) > 0 && (!offer.subscription || Number(offer.sellingPlanId) > 0) && (offer.gifts || []).every((gift) => /^\d+$/.test(String(gift.variantId || ''))); }
    addOffer(offer) {
      const token = ++this.nextAdd;
      if (this.previewMode !== true && this.isConfigured(offer)) this.pendingAdds.set(token, clone(offer));
      const result = this.enqueue(async () => {
        const configured = this.isConfigured(offer);
        if (!configured && this.previewMode === false) throw new Error('configuration_error');
        if (this.previewMode === true || !configured) {
          this.preview = true;
          const id = String(offer.offerId || offer.id) + (offer.subscription ? `:subscription-${offer.sellingPlanId || offer.subscription.days}` : '');
          const previous = this.previewOffers.get(id);
          this.previewOffers.set(id, { offer: clone(offer), count: (previous?.count || 0) + 1 });
          return this.renderPreview();
        }
        this.preview = false;
        const units = Math.max(1, Number(offer.quantity) || 1);
        const properties = offerProperties(offer);
        const gifts = readJSON(properties._pr_gifts, []);
        try {
          const items = [{ id: Number(offer.variantId), quantity: units, properties }];
          if (offer.subscription) items[0].selling_plan = Number(offer.sellingPlanId);
          const parent = offerKey({ variant_id: offer.variantId, properties });
          gifts.forEach(gift => items.push({ id: Number(gift.id), quantity: gift.quantity, properties: { _pr_gift: 'true', _pr_parent: parent } }));
          const generation = this.invalidations;
          const added = await this.request('cart/add.js', { items, sections: CART_SECTION });
          await this.reconcile(false, cartFromResponse(added), generation);
          this.pendingAdds.delete(token);
          this.emit();
          return this.cart;
        } catch (error) {
          await this.read(false).catch(() => {});
          this.pendingAdds.delete(token);
          this.emit();
          throw error;
        }
      });
      if (this.pendingAdds.has(token)) this.emit();
      return result;
    }
    renderPreview() {
      const items = [];
      for (const [id, { offer, count }] of this.previewOffers) {
        if (count < 1) continue;
        const units = Math.max(1, Number(offer.quantity) || 1);
        const price = Math.round(Number(offer.unitPrice) || 0) * units * count;
        const original = Math.round(Number(offer.compareAtPrice) || Number(offer.unitPrice) || 0) * units * count;
        const key = `preview:${id}`;
        items.push({ key, variant_id: id, title: offer.title || offer.label, product_title: offer.title || offer.label, image: offer.image, quantity: units * count, final_line_price: price, original_line_price: original, properties: { _pr_offer: id, _pr_units: String(units), _pr_label: offer.label || '', _pr_benefits: offer.benefits ? JSON.stringify(offer.benefits) : '' }, preview_subscription_label: offer.subscription?.label || '' });
        (offer.gifts || []).forEach((gift, index) => items.push({ key: `${key}:gift:${index}`, variant_id: gift.variantId || `${id}-gift-${index}`, title: gift.title, product_title: gift.title, image: gift.image, quantity: Math.max(1, Number(gift.quantity) || 1) * count, final_line_price: 0, original_line_price: Math.round(Number(gift.price) || 0) * Math.max(1, Number(gift.quantity) || 1) * count, properties: { _pr_gift: 'true', _pr_parent: `${id}:${id}` } }));
      }
      const currency = [...this.previewOffers.values()][0]?.offer.currency || this.previewCurrency;
      this.cart = { items, total_price: items.reduce((sum, item) => sum + item.final_line_price, 0), original_total_price: items.reduce((sum, item) => sum + item.original_line_price, 0), item_count: items.reduce((sum, item) => sum + item.quantity, 0), currency };
      this.onUpdate(this.cart, true);
      return this.cart;
    }
    change(target, count) {
      return this.enqueue(async () => {
        if (this.preview) {
          const id = String(target.properties?._pr_offer || target.variant_id);
          const entry = this.previewOffers.get(id);
          if (entry) { entry.count = Math.max(0, count); if (!entry.count) this.previewOffers.delete(id); }
          return this.renderPreview();
        }
        // Speed: the first read only refreshes line keys, so it is skipped when the server just confirmed the cart.
        const confirmed = this.isConfirmed();
        const apply = async () => {
          const current = target.group ? rowsFor(this.cart).find((row) => row.group === target.group) : rowsFor(this.cart).find((row) => row.key === target.key);
          if (!current) return undefined;
          const desired = Math.max(0, count) * current.units;
          if (current.keys.length === 1) return this.request('cart/change.js', { id: current.keys[0], quantity: desired });
          const updates = Object.fromEntries(current.keys.map((key, index) => [key, index ? 0 : desired]));
          return this.request('cart/update.js', { updates });
        };
        return this.mutate(async () => {
          if (!confirmed) { await this.read(); return apply(); }
          try { return await apply(); }
          catch (error) {
            // Shopify refused (e.g. a line key changed): do what the skipped read would have done, then retry once.
            if (!error.rejected) throw error;
            await this.read();
            return apply();
          }
        });
      });
    }
    addUpsell(upsell) {
      if (this.preview || !upsell.variantId) return this.addOffer({ ...upsell, unitPrice: upsell.previewPrice, compareAtPrice: upsell.previewCompare, id: `upsell-${upsell.id}`, offerId: `upsell-${upsell.id}`, quantity: 1, variantId: null, gifts: [] });
      return this.enqueue(() => this.mutate(() => this.request('cart/add.js', { items: [{ id: Number(upsell.variantId), quantity: 1 }] })));
    }
    // `reconcile` (drawer opening) runs checkout's gift check now, so the Checkout tap need not wait for it.
    refresh(reconcile = false) { return this.enqueue(() => this.preview ? Promise.resolve(this.renderPreview()) : reconcile ? this.reconcile() : this.read()); }
    async checkoutReady() {
      if (this.preview) return false;
      // Speed: when the cart on screen is the one the server confirmed (on drawer open or after the last change)
      // and nothing is pending, the check uses it instead of waiting for another identical cart.js read.
      if (!(this.pending === 0 && this.isConfirmed())) await this.enqueue(() => this.reconcile());
      return this.cart.items.length > 0 && !this.cart.items.some((item) => isGift(item) && item.final_line_price > 0);
    }
  }
  // Theme-editor preview frames are replaced on Save. Only editor state belongs in storage.
  const editorStorageKey = () => scope.Shopify?.designMode && scope.Shopify?.theme?.id
    ? `pr-cart-editor:v1:${scope.Shopify.theme.id}` : null;
  function readEditorState() {
    try {
      const key = editorStorageKey();
      const state = key && JSON.parse(scope.sessionStorage.getItem(key) || 'null');
      return state?.version === 1 ? state : null;
    } catch (_) { return null; }
  }
  function saveEditorState(engine, open) {
    try {
      const key = editorStorageKey();
      if (!key) return;
      scope.sessionStorage.setItem(key, JSON.stringify({ version: 1, open: Boolean(open),
        previewMode: engine.previewMode, previewCurrency: engine.previewCurrency,
        preview: engine.preview, offers: engine.preview ? [...engine.previewOffers] : [] }));
    } catch (_) { /* Storage may be unavailable in an embedded preview. */ }
  }
  function restoreEditorOffers(engine, state) {
    if (!state?.preview || state.previewMode !== engine.previewMode ||
        state.previewCurrency !== engine.previewCurrency || !Array.isArray(state.offers)) return;
    const entries = state.offers.filter(entry => Array.isArray(entry) && typeof entry[0] === 'string' &&
      entry[1]?.offer && typeof entry[1].offer === 'object' && !Array.isArray(entry[1].offer) &&
      Number.isSafeInteger(entry[1].count) && entry[1].count > 0);
    engine.preview = true;
    engine.previewOffers = new Map(entries);
    engine.renderPreview();
  }
  const session = scope.PureRevivalCart?.session || {};
  const exported = { CartEngine, giftPlan, rowsFor, isGift, presentationKey, offerProperties, session, readEditorState, saveEditorState, restoreEditorOffers };
  if (typeof module !== 'undefined' && module.exports) module.exports = exported;
  scope.PureRevivalCart = exported;
  if (!scope.document || !scope.customElements) return;
  const escape = (value) => String(value ?? '').replace(/[&<>"']/g, (match) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[match]));
  class PrCartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.contentObserver) return;
      this.lifecycleController = new AbortController();
      this.contentObserver = new MutationObserver(() => this.initializeWhenReady());
      this.contentObserver.observe(this, { childList: true, subtree: true, characterData: true });
      document.addEventListener('DOMContentLoaded', () => this.initializeWhenReady(), { signal: this.lifecycleController.signal });
      document.addEventListener('shopify:section:load', (event) => { if (event.target.contains(this)) this.initializeWhenReady(); }, { signal: this.lifecycleController.signal });
      queueMicrotask(() => this.initializeWhenReady());
    }
    initializeWhenReady() {
      if (!this.isConnected || !this.contentObserver) return;
      const configNode = this.querySelector('[data-pr-cart-config]');
      const cartNode = this.querySelector('.cart');
      const source = configNode?.textContent?.trim();
      if (!source || !cartNode) return;
      if (this.controller && this.configNode === configNode && this.cartNode === cartNode && this.configSource === source) return;
      const required = ['.cart-ov', '[data-pr-cart-error]', '.cart-body', '.cart-f', '.c-ship', '.c-uc', '[data-pr-cart-count-text]', '[data-pr-cart-total]', '[data-pr-cart-rows]', '[data-pr-cart-empty]', '[data-pr-upsells]', '[data-pr-shipping-text]', '.c-fill', '.c-track', '.c-cap'];
      if (required.some((selector) => !this.querySelector(selector))) return;
      let config;
      try { config = JSON.parse(source); } catch (_) { return; }
      if (!config || typeof config !== 'object' || Array.isArray(config)) return;
      this.configNode = configNode;
      this.cartNode = cartNode;
      this.configSource = source;
      this.initialize(config);
    }
    initialize(config) {
      clearTimeout(this.toastTimeout);
      this.controller?.abort();
      this.controller = new AbortController();
      this.config = config;
      this.strings = this.config.strings || {};
      const previewCurrency = scope.PR?.previewCurrency || 'USD';
      const previous = this.engine || session.engine;
      const previewMode = scope.Shopify?.designMode === true || scope.PR?.preview;
      this.engine = previous && previous.previewMode === previewMode && previous.previewCurrency === previewCurrency ? previous : new CartEngine({ root: scope.Shopify?.routes?.root || '/', currency: this.config.currency, previewCurrency, previewMode, initialCart: this.config.cart });
      const savedEditorState = readEditorState();
      if (!previous) restoreEditorOffers(this.engine, savedEditorState);
      session.engine = this.engine;
      session.owner = this;
      this.engine.onUpdate = (cart, preview) => { if (this.isActive()) { this.render(cart, preview); saveEditorState(this.engine, this.isOpen); } };
      this.engine.onBusy = (busy) => { if (this.isActive()) this.busy(busy); };
      this.rows = [];
      this.upsells = this.config.upsells || [];
      const signal = this.controller.signal;
      document.addEventListener('pr:cart-add', (event) => {
        this.clearError();
        const addition = this.engine.addOffer(event.detail.offer);
        this.open(false);
        addition.then(() => { if (!signal.aborted) document.dispatchEvent(new CustomEvent('pr:cart-added', { detail: { offerId: event.detail.offer.offerId, preview: this.engine.preview } })); }).catch((error) => { if (!signal.aborted) this.error(error); });
      }, { signal });
      document.addEventListener('pr:cart-open', () => this.open(), { signal });
      // Speed: the product script can now start first; it delivers an Add tapped before this point on this event.
      queueMicrotask(() => { if (!signal.aborted) document.dispatchEvent(new CustomEvent('pr:cart-ready')); });
      document.addEventListener('click', (event) => {
        if (event.target.closest('[data-pr-cart-open]')) { event.preventDefault(); this.open(); }
      }, { signal });
      this.addEventListener('click', (event) => this.click(event), { signal });
      document.addEventListener('keydown', (event) => this.keydown(event), { signal });
      document.addEventListener('shopify:block:select', (event) => { if (this.contains(event.target)) this.open(false); }, { signal });
      document.addEventListener('shopify:section:select', (event) => { if (event.target.contains(this)) this.open(); }, { signal });
      // Speed: the first pageshow duplicated the refresh just below; only pages restored from the back/forward cache need it.
      scope.addEventListener('pageshow', (event) => { if (event.persisted && !this.engine.preview) this.engine.refresh().catch((error) => this.error(error)); }, { signal });
      // Another tab may change the cart while this one is hidden, so Checkout re-checks it afterwards.
      document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') this.engine.markStale(); }, { signal });
      scope.addEventListener('blur', () => this.engine.markStale(), { signal });
      this.render(this.engine.cart, this.engine.preview);
      if (session.reopen || this.isOpen || (!previous && savedEditorState?.open)) { session.reopen = false; this.open(false); }
      // Speed: the product buttons now start listening before this background refresh begins. Its "busy" start is
      // not announced to them, so Add To Cart keeps its normal label and stays tappable, as it always did.
      this.quietBusy = true;
      this.engine.refresh().catch((error) => this.error(error));
      this.quietBusy = false;
      if (!session.warmed) {
        session.warmed = true;
        const idle = scope.requestIdleCallback || ((callback) => setTimeout(callback, 200));
        (scope.PR?.afterHero || ((run) => run()))(() => idle(() => this.warmImages(), { timeout: 2000 }));
      }
    }
    // Speed: once the main image has loaded and the page is idle, fetch and decode the exact images the drawer
    // shows for each bundle (product, gifts, benefits) and its review photo, so none of them pops in when it opens.
    warmImages() {
      const urls = new Set();
      document.querySelectorAll('[data-pr-offer]').forEach((element) => {
        element.querySelectorAll('[data-pr-offer-config],[data-pr-gift-config],[data-pr-benefit-config]').forEach((node) => {
          const image = readJSON(node.textContent, null)?.image;
          if (typeof image === 'string' && image) urls.add(image);
        });
      });
      this.querySelectorAll('img[loading="lazy"]').forEach((image) => { image.loading = 'eager'; });
      session.warmedImages = [...urls].map((url) => {
        const image = new Image();
        image.decoding = 'async';
        image.src = url;
        image.decode?.().catch(() => {});
        return image;
      });
    }
    disconnectedCallback() {
      clearTimeout(this.toastTimeout);
      this.contentObserver?.disconnect();
      this.contentObserver = null;
      this.lifecycleController?.abort();
      this.lifecycleController = null;
      this.controller?.abort();
      this.controller = null;
      if (session.owner === this) {
        session.reopen = Boolean(this.isOpen);
        session.owner = null;
        if (this.isOpen) document.body.classList.remove('cart-open');
      }
      this.isOpen = false;
    }
    isActive() { return this.isConnected && this.controller && !this.controller.signal.aborted && session.owner === this; }
    t(key, values = {}) { let text = this.strings[key] || key; for (const [name, value] of Object.entries(values)) text = text.replaceAll(`{${name}}`, String(value)); return text; }
    money(value, full = false) { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.engine.cart.currency || 'USD', ...(full ? {} : { maximumFractionDigits: value % 100 ? 2 : 0 }) }).format(value / 100); }
    busy(busy) {
      this.setAttribute('aria-busy', String(busy));
      this.querySelectorAll('[data-pr-mutate]').forEach((button) => { button.disabled = busy; });
      this.querySelectorAll('[data-pr-minimum]').forEach((button) => { button.disabled = true; });
      const checkout = this.querySelector('[data-pr-checkout]');
      if (checkout) checkout.disabled = busy || this.invalidGift || !this.engine.cart.items.length;
      if (!this.quietBusy) document.dispatchEvent(new CustomEvent('pr:cart-busy', { detail: { busy } }));
    }
    clearError() { const node = this.querySelector('[data-pr-cart-error]'); node.textContent = ''; node.hidden = true; }
    error(error) {
      if (!this.isActive()) return;
      const node = this.querySelector('[data-pr-cart-error]');
      const message = error?.message;
      node.textContent = message === 'configuration_error' ? this.t('configuration') : message && message !== 'cart_error' && message !== 'Failed to fetch' ? message : this.t('error');
      node.hidden = false;
      document.dispatchEvent(new CustomEvent('pr:cart-error', { detail: { message: node.textContent } }));
    }
    open(refresh = true) {
      if (!this.isActive()) return;
      if (!this.isOpen) this.lastFocus = document.activeElement;
      this.isOpen = true;
      saveEditorState(this.engine, true);
      this.querySelector('.cart').classList.add('open');
      this.querySelector('.cart-ov').classList.add('open');
      this.querySelector('.cart').setAttribute('aria-hidden', 'false');
      this.querySelector('.cart').removeAttribute('inert');
      document.body.classList.add('cart-open');
      this.querySelector('[data-pr-cart-close]')?.focus();
      if (refresh) this.engine.refresh(true).catch((error) => this.error(error));
    }
    close() {
      this.isOpen = false;
      saveEditorState(this.engine, false);
      this.querySelector('.cart').classList.remove('open');
      this.querySelector('.cart-ov').classList.remove('open');
      this.querySelector('.cart').setAttribute('aria-hidden', 'true');
      this.querySelector('.cart').setAttribute('inert', '');
      document.body.classList.remove('cart-open');
      this.lastFocus?.focus();
    }
    keydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') { event.preventDefault(); this.close(); }
      if (event.key !== 'Tab') return;
      const nodes = [...this.querySelector('.cart').querySelectorAll('button:not([disabled]),a[href],input:not([disabled]),[tabindex="0"]')].filter((node) => node.getClientRects().length);
      const first = nodes[0], last = nodes.at(-1);
      if (event.shiftKey && (document.activeElement === first || !this.contains(document.activeElement))) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !this.contains(document.activeElement))) { event.preventDefault(); first?.focus(); }
    }
    click(event) {
      if (event.target.closest('[data-pr-cart-close],.cart-ov')) { this.close(); return; }
      const action = event.target.closest('[data-pr-cart-action]');
      if (action) {
        const row = this.rows[Number(action.dataset.row)];
        if (!row) return;
        const count = action.dataset.prCartAction === 'remove' ? 0 : Math.max(0, row.count + (action.dataset.prCartAction === 'increase' ? 1 : -1));
        this.clearError();
        this.engine.change(row, count).then(() => this.querySelector(`[data-row="${action.dataset.row}"][data-pr-cart-action="${action.dataset.prCartAction}"]`)?.focus()).catch((error) => this.error(error));
      }
      const upsell = event.target.closest('[data-pr-upsell-add]');
      if (upsell) { this.clearError(); this.engine.addUpsell(this.upsells[Number(upsell.dataset.prUpsellAdd)]).catch((error) => this.error(error)); }
      const arrow = event.target.closest('[data-pr-upsell-step]');
      if (arrow) { const track = this.querySelector('.c-uc'); const first = track?.children[0]; const next = track?.children[1]; track?.scrollBy({ left: Number(arrow.dataset.prUpsellStep) * (next ? next.offsetLeft - first.offsetLeft : track.clientWidth), behavior: 'smooth' }); }
      if (event.target.closest('[data-pr-checkout]')) {
        this.clearError();
        if (this.engine.preview) { this.toast(this.t('previewCheckout')); return; }
        this.engine.checkoutReady().then((ready) => { if (ready) scope.location.assign(`${this.engine.root}checkout`); else this.error(new Error(this.engine.preview ? this.t('preview') : this.t('giftError'))); }).catch((error) => this.error(error));
      }
    }
    toast(message) {
      const node = this.querySelector('[data-pr-cart-toast]');
      if (!node) return;
      clearTimeout(this.toastTimeout);
      node.textContent = message;
      node.classList.add('on');
      this.toastTimeout = setTimeout(() => node.classList.remove('on'), 3200);
    }
    thumbnail(item, gift = false) {
      let image = item.image || item.featured_image?.url;
      // Speed: cart.js lists images on cdn.shopify.com, the page's own cart JSON on this store's /cdn/shop/files/.
      // Both serve byte-identical files; one address means a row never downloads its image twice.
      if (typeof image === 'string') image = image.replace(/^https:\/\/cdn\.shopify\.com\/s\/files\/\d+\/\d+\/\d+\/\d+\/files\//, `//${scope.location.host}/cdn/shop/files/`);
      if (!gift && item.properties?._pr_plan_id && item.properties?._pr_image) {
        try {
          const candidate = new URL(item.properties._pr_image, scope.location.origin);
          if (candidate.protocol === 'https:' && (candidate.hostname === 'cdn.shopify.com' || (candidate.origin === scope.location.origin && candidate.pathname.startsWith('/cdn/')))) image = candidate.href;
        } catch (_) {}
      }
      if (!image && item.benefitKind) {
        const shape = item.benefitKind === 'truck' ? '<path d="M3 6h11v11H3zM14 10h4l3 4v3h-7"/><circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/>' : '<path d="M12 3l8 3v6c0 5-8 9-8 9S4 17 4 12V6zM8 12l3 3 5-6"/>';
        return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${shape}</svg>`;
      }
      const media = image ? `<img src="${escape(image)}" alt="${escape(item.product_title || item.title)}" width="184" height="184" loading="lazy">` : `<svg viewBox="0 0 100 100" aria-hidden="true"><use href="#${gift ? 'cd-dk' : 'cd-pk'}" x="10" y="4" width="80" height="92"/></svg>`;
      return media;
    }
    stars() { return '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="m10 1 2.7 5.7 6.3.9-4.5 4.4 1.1 6.2-5.6-3-5.6 3 1.1-6.2L1 7.6l6.3-.9Z" fill="currentColor"/></svg>'.repeat(5); }
    rowHTML(row, index) {
      const gift = isGift(row);
      const free = gift && row.final_line_price === 0;
      const comparison = row.compare_line_price ?? row.original_line_price;
      const saved = comparison - row.final_line_price;
      const label = row.properties?._pr_label || (row.variant_title && row.variant_title !== 'Default Title' ? row.variant_title : '');
      const proof = !gift && row.group && this.config.showProof ? `<div class="c-proof"><span class="c-st" role="img" aria-label="${escape(this.t('rating'))}">${this.stars()}</span><span>${escape(this.config.proofText)}</span></div>` : '';
      const planLabel = !gift && (row.selling_plan_allocation?.selling_plan?.name || (this.engine.preview && row.preview_subscription_label));
      const plan = planLabel ? `<div class="c-plan"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 11a8 8 0 0 0-14.5-3.5L4 9"/><path d="M4 4v5h5"/><path d="M4 13a8 8 0 0 0 14.5 3.5L20 15"/><path d="M20 20v-5h-5"/></svg>${escape(planLabel)}</div>` : '';
      return `<div class="c-row${gift ? ' c-gift' : ''}"><div class="c-img">${this.thumbnail(row, gift)}</div><div>${proof}<div class="c-title">${escape(row.properties?._pr_plan_id && row.properties?._pr_title || row.product_title || row.title)}</div>${!free ? `<div class="c-sub">${escape(label)}</div>` : ''}${plan}${row.benefitKind !== 'shield' ? `<div class="c-price">${free ? `<span class="c-free-price">${escape(this.t('free'))}</span>` : `<b>${escape(this.money(row.final_line_price))}</b>`}${saved > 0 ? `<span class="c-compare-saving"><s>${escape(this.money(comparison))}</s><span class="c-save">${escape(this.t('saving', { amount: this.money(saved) }))}</span></span>` : ''}</div>` : ''}${!gift ? `<div class="c-ctl"><div class="c-qty"><button type="button" data-pr-mutate ${row.count <= 1 ? 'data-pr-minimum disabled' : ''} data-row="${index}" data-pr-cart-action="decrease" aria-label="${escape(this.t('decrease'))}">−</button><span aria-live="polite">${row.count}</span><button type="button" data-pr-mutate data-row="${index}" data-pr-cart-action="increase" aria-label="${escape(this.t('increase'))}">+</button></div><button class="c-rm" type="button" data-pr-mutate data-row="${index}" data-pr-cart-action="remove">${escape(this.t('remove'))}</button></div>` : ''}</div></div>`;
    }
    render(cart, preview) {
      const benefitsByOffer = {};
      const offersByKey = {};
      document.querySelectorAll('[data-pr-offer]').forEach(element => {
        try {
          const base = JSON.parse(element.querySelector('[data-pr-offer-config]').textContent);
          base.benefits = [...element.querySelectorAll('[data-pr-benefit-config]')].map(node => JSON.parse(node.textContent));
          base.gifts = [...element.querySelectorAll('[data-pr-gift-config]')].map(node => JSON.parse(node.textContent));
          benefitsByOffer[element.dataset.offerId] = base.benefits;
          offersByKey[presentationKey(base.variantId, base.quantity)] = { ...base, subscriptionManualGifts: false };
          if (base.sellingPlanId && base.subscriptionQuantity) offersByKey[presentationKey(base.subscriptionVariantId, base.subscriptionQuantity, base.sellingPlanId)] = { ...base, compareAtPrice: base.compareAtPrice * base.quantity / base.subscriptionQuantity, giftIncludedInBundle: base.subscriptionGiftIncludedInBundle };
        } catch (_) {}
      });
      this.rows = rowsFor(cart, benefitsByOffer, offersByKey);
      this.querySelector('[data-pr-cart-rows]').innerHTML = this.rows.map((row, index) => this.rowHTML(row, index)).join('');
      this.querySelector('[data-pr-cart-empty]').hidden = cart.items.length > 0;
      this.querySelector('.cart-f').hidden = !cart.items.length;
      this.invalidGift = !preview && cart.items.some((item) => isGift(item) && item.final_line_price > 0);
      if (this.invalidGift) this.error(new Error(this.t('giftError')));
      const count = this.rows.filter((row) => this.config.countGifts || !isGift(row)).reduce((total, row) => total + row.count, 0);
      this.querySelector('[data-pr-cart-count-text]').textContent = this.config.showCount ? this.t('itemCount', { count }) : '';
      const comparisonTotal = this.rows.filter(row => !row.benefitKind).reduce((sum, row) => sum + (row.compare_line_price ?? row.original_line_price ?? row.final_line_price), 0);
      this.querySelector('[data-pr-cart-total]').innerHTML = `${this.config.showCompare && comparisonTotal > cart.total_price ? `<s>${escape(this.money(comparisonTotal))}</s>` : ''}<b>${escape(this.money(cart.total_price))}</b>`;
      document.querySelectorAll('[data-pr-cart-count]').forEach((node) => { node.textContent = count; node.classList.toggle('on', count > 0); node.hidden = count === 0; });
      const threshold = preview ? Number(this.config.previewThreshold) : Number(this.config.shippingThreshold);
      const bundleShipping = this.rows.some(row => row.benefitKind === 'truck');
      const showShipping = cart.items.length && (bundleShipping || (threshold > 0 && this.config.shippingEnabled && (preview || this.config.shippingCurrency === cart.currency)));
      this.querySelector('.c-ship').hidden = !showShipping;
      if (showShipping) {
        const left = bundleShipping ? 0 : Math.max(0, threshold - cart.total_price), percent = bundleShipping ? 100 : Math.min(100, Math.round(cart.total_price / threshold * 100));
        const message = this.querySelector('[data-pr-shipping-text]');
        const values = { amount: this.money(left, true), shipping: this.t('shippingFree') };
        message.innerHTML = left ? escape(this.t('shippingRemaining')).replace(/\{(amount|shipping)\}/g, (_, key) => `<b>${escape(values[key])}</b>`) : `<b>${escape(this.t('shippingUnlocked'))}</b>`;
        message.classList.toggle('prog', left > 0);
        this.querySelector('.c-fill').style.width = `${percent}%`;
        this.querySelector('.c-track').setAttribute('aria-valuenow', percent);
        this.querySelector('.c-cap').classList.toggle('done', !left);
      }
      const available = this.upsells.map((upsell, index) => ({ upsell, index })).filter(({ upsell }) => (preview || (upsell.variantId && upsell.available)) && !cart.items.some((item) => String(item.variant_id) === String(upsell.variantId) || item.properties?._pr_offer === `upsell-${upsell.id}`));
      const upsellRoot = this.querySelector('[data-pr-upsells]');
      upsellRoot.hidden = !cart.items.length || !available.length;
      upsellRoot.classList.toggle('one', available.length === 1);
      this.querySelector('.c-uc').innerHTML = available.map(({ upsell, index }) => `<div class="c-u" data-block="${escape(upsell.id)}"><div class="c-ui">${this.thumbnail(upsell, upsell.kind === 'gift')}</div><div><b class="c-un">${escape(upsell.title)}</b><div class="c-ud">${escape(upsell.description)}</div><div class="c-upr">${escape(this.money(preview ? upsell.previewPrice : upsell.unitPrice))}${(preview ? upsell.previewCompare : upsell.compareAtPrice) > (preview ? upsell.previewPrice : upsell.unitPrice) ? `<s>${escape(this.money(preview ? upsell.previewCompare : upsell.compareAtPrice))}</s>` : ''}</div></div><button type="button" class="c-add" data-pr-mutate data-pr-upsell-add="${index}">${escape(this.t('add'))}</button></div>`).join('');
      this.busy(this.engine.pending > 0);
      if (!preview) document.querySelectorAll('.pr-cart-page').forEach((page) => {
        page.querySelector('.pr-cart-page__items').innerHTML = cart.items.map((item) => `<article class="pr-cart-page__row"><div>${this.thumbnail(item, isGift(item))}</div><div><h2>${escape(item.product_title || item.title)}</h2><p>${escape(this.t('quantity'))}: ${item.quantity}</p><p>${escape(this.money(item.final_line_price))}</p></div></article>`).join('');
        page.querySelector('[data-pr-page-empty]').hidden = cart.items.length > 0;
        page.querySelector('.pr-cart-page__total').hidden = !cart.items.length;
        page.querySelector('[data-pr-page-total]').textContent = `${this.t('subtotal')}: ${this.money(cart.total_price)}`;
      });
      document.dispatchEvent(new CustomEvent('pr:cart-updated', { detail: { cart, count, preview } }));
    }
  }
  if (!customElements.get('pr-cart-drawer')) customElements.define('pr-cart-drawer', PrCartDrawer);
})(typeof window !== 'undefined' ? window : globalThis);
